import React, { useRef, useMemo, useLayoutEffect, useState } from 'react';
import { styled } from '@compiled/react';
import omit from 'lodash/omit';
import { di } from 'react-magnetic-di';
import { EditorCardProvider } from '@atlaskit/smart-card';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { CardPlugin } from '@atlaskit/editor-plugin-card';
import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { withFormField } from '@atlassian/jira-issue-create-commons/src/common/ui/with-form-field/index.tsx';
import { AsyncRichTextEditView } from '@atlassian/jira-issue-field-rich-text/src/async.tsx';
import { useShouldBlockStorageUploads } from '@atlassian/jira-storage-hard-limits/src/services/use-should-block-storage-uploads/useShouldBlockStorageUploads.tsx';
import { StorageLimitsEditorBanner } from '@atlassian/jira-storage-hard-limits/src/ui/editor-banner/index.tsx';
import { UserTeamProjectMentionProviderWithResolverMentionName } from '@atlassian/jira-mention-provider/src/services/user-team-project-mention-provider-with-name-resolver/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	getProductForEditorAIPlugin,
	useEditorConfig,
} from '@atlassian/jira-platform-utils-shared-editor-config/src/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import { useExtractedLinksRef } from '@atlassian/editor-ai-common/utils/useExtractedLinksRef';
// Disabling this until isGICPositioningEnabled ff is cleaned up
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { AsyncAiProactiveDescriptionCompleter } from '@atlassian/jira-ai-proactive-description-completer/async.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIsGICPositioningEnabled } from '../../../../controllers/gic-positioning-enabled/main.tsx';
import type { Props, DescriptionFieldConfig, FieldValue } from './types.tsx';
import { validateDescriptionField } from './utils.tsx';
import { messages } from './messages.tsx';

const skeletonHeight = '150px';

const PRINCIPAL_ID = 'context';

/**
 * We use the same DescriptionField component for all fields of "paragraph" type, including custom fields
 * This constant is for the default issue description field name
 */
const DESCRIPTION_FIELD_DEFAULT_FIELD_NAME = 'Description';
const DESCRIPTION_FIELD_EXPERIENCE = 'GIC';

export const DescriptionField = (props: Props) => {
	di(window);
	const {
		width = '100%',
		fieldProps: { value, onChange, onBlur },
		selectedProject,
		defaultValue,
		additionalData,
		fieldName,
		autoFocus = false,
		isReadOnly = false,
		selectedIssueType,
		getFormState,
		storageLimitsData,
	} = props;

	const { formatMessage } = useIntl();
	const { data: orgId } = useOrgId();
	const cloudId = useCloudId();
	const loggedInUserAccountId = useAccountId();

	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	// TODO Add this field with the fields on previous line when cleaning FF
	const { onFocus: onFocusField, onBlur: onBlurField } = isGICPositioningEnabled() ? props : {};

	const { mediaContext, adminRichTextConfig } = additionalData;
	let mediaUploadContext;
	const uploadToken = mediaContext?.uploadToken;

	if (uploadToken) {
		mediaUploadContext = {
			clientId: uploadToken.clientId,
			serviceHost: uploadToken.endpointUrl,
			collection: uploadToken.targetCollection,
			token: uploadToken.token,
		};
	}

	const [editorApi, setEditorApi] = useState<PublicPluginAPI<[CardPlugin]> | undefined>();

	const product = getProductForEditorAIPlugin(selectedProject?.projectType);

	const issueTypeLabel = selectedIssueType?.label?.toLowerCase();

	const allowIssueReformatter = useMemo(
		() => fieldName === DESCRIPTION_FIELD_DEFAULT_FIELD_NAME,
		[fieldName],
	);

	const productConfig = useMemo(
		() => ({
			product: 'jira',
			cloudId,
		}),
		[cloudId],
	);

	const linkPicker = useLinkPickerEditorProps(undefined, productConfig);

	const descriptionValue =
		value && typeof value === 'object' && 'type' in value ? value : undefined;
	const extractedLinksRef = useExtractedLinksRef(descriptionValue);

	const editorPluginAIConfig = useMemo(
		() => ({
			editorPluginAIConfig: {
				product,
				isAiOptInEnabled: !!adminRichTextConfig?.aiEnabledByProject,
				// For more context about allowIssueReformatter option, please refer to this PR: https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/91592/overview
				allowIssueReformatter,
				getAdditionalProps: () => {
					const linkedUrls = extractedLinksRef.current?.();

					return {
						issue_type_label: issueTypeLabel,
						issue_summary: getFormState?.()?.values.summary || '',
						...(linkedUrls ? { linked_urls: linkedUrls } : {}),
					};
				},
				aiLifeCycleDynamicAttributesGetter: () => ({
					issueTypeLabel: issueTypeLabel || '',
				}),
				linking: linkPicker
					? {
							linkPicker,
							smartLinks: {
								provider: Promise.resolve(new EditorCardProvider()),
							},
						}
					: undefined,
				getChannelVariables: (purpose: string) => {
					if (purpose === 'rovo-agent') {
						if (typeof cloudId === 'string' && typeof loggedInUserAccountId === 'string')
							return {
								cloudId,
								userId: loggedInUserAccountId,

								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								contentURL: window.location.origin + window.location.pathname,
							};
					}
					return null;
				},
				actionSideEffects: {
					continueInChat: () => {
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						const dockButton = document.getElementById(
							'minimizable-modal.ui.modal-container.modal-header.mini-button',
						);
						dockButton?.click?.();
					},
				},
			},
		}),
		[
			product,
			adminRichTextConfig?.aiEnabledByProject,
			allowIssueReformatter,
			linkPicker,
			issueTypeLabel,
			getFormState,
			cloudId,
			loggedInUserAccountId,
			extractedLinksRef,
		],
	);

	const editorConfig = useEditorConfig(editorPluginAIConfig);

	const mentionProvider = useRef(
		new UserTeamProjectMentionProviderWithResolverMentionName(
			{
				projectKey: toProjectKey(`${selectedProject?.projectKey || ''}`),
				projectType: selectedProject?.projectType,
			},
			{ userId: PRINCIPAL_ID, cloudId, orgId },
		),
	);
	const editorKey = useRef(Date.now());

	const shouldBlockStorageUploads = fg('jira-storage-hard-limit-gate')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useShouldBlockStorageUploads({
				attachmentStorageAllowed: storageLimitsData?.attachmentStorageAllowed ?? undefined,
				attachmentStorageUsed: storageLimitsData?.attachmentStorageUsed ?? undefined,
				attachmentStorageIsUnlimited: storageLimitsData?.attachmentStorageIsUnlimited ?? undefined,
			})
		: false;

	const mediaProvider = {
		viewContext: mediaUploadContext,
		uploadContext: shouldBlockStorageUploads ? null : mediaUploadContext,
	};

	let actualValue = defaultValue;

	// We are force resetting the editor using key as we could not achieve using EditorActions.replaceDocument or clear
	// We tried to pass down ref to the DescriptionEdit component and call the replaceDocument from here. Which did not work.
	// If we find a better way to achieve this, this code inside should be replaced.
	useLayoutEffect(() => {
		// @ts-expect-error - TS2339 - Property 'resetRequired' does not exist on type 'DocNode | ResetDescriptionValue | OverrideDescriptionValue'.
		if (value?.resetRequired === true) {
			editorKey.current = Date.now();
			// @ts-expect-error - TS2322 - Type 'Pick<DocNode | ResetDescriptionValue | OverrideDescriptionValue, never> | undefined' is not assignable to type 'FieldValue'.
			onChange(omit(value, 'resetRequired'));
		}
	}, [defaultValue, onChange, value]);

	// This is a similar hack to the one you see above for the case when we don't want to reset the editor to the
	// initial value but instead override the existing one with new one. It's needed for UI modifications setValue to work.
	// As above: once a better way is found, this code should be replaced.
	// UNBOX-505
	// @ts-expect-error - TS2339 - Property 'overrideRequired' does not exist on type 'DocNode | ResetDescriptionValue | OverrideDescriptionValue'.
	const overrideRequired = value?.overrideRequired || false;
	// @ts-expect-error - TS2322 - Type 'Pick<DocNode | ResetDescriptionValue | OverrideDescriptionValue, never> | undefined' is not assignable to type 'FieldValue'.
	actualValue = overrideRequired ? omit(value, 'overrideRequired') : (value ?? defaultValue);

	// Since this useEffect call is in the feature-flag condition we can ignore eslint complaints
	useLayoutEffect(() => {
		if (overrideRequired === true) {
			editorKey.current = Date.now();
			onChange(actualValue);
		}
	}, [overrideRequired, onChange, actualValue]);

	const issueSummary = getFormState?.()?.values.summary || '';
	const issueDescription =
		value && typeof value === 'object' && 'type' in value ? value : undefined;
	const isDescriptionCompleterEnabled =
		adminRichTextConfig?.aiEnabledByProject &&
		expVal('experiment_proactive_desc_completer_issueview', 'isEnabled', false);

	const placeholderText = useMemo(() => {
		if (fg('thor_support_google_docs_in_description_improver')) {
			return formatMessage(messages.placeholderWithGoogleDocs);
		}
		return formatMessage(messages.placeholder);
	}, [formatMessage]);

	const contentComponents = useMemo(() => {
		if (shouldBlockStorageUploads) {
			return {
				before: [],
				after: <StorageLimitsEditorBanner />,
			};
		}

		if (isDescriptionCompleterEnabled) {
			return {
				before: [],
				after: (
					<AsyncAiProactiveDescriptionCompleter
						editorApi={editorApi}
						editorExperience={DESCRIPTION_FIELD_EXPERIENCE}
						issueType={issueTypeLabel}
						issueSummary={issueSummary}
						issueDescription={issueDescription}
					/>
				),
			};
		}

		return undefined;
	}, [
		editorApi,
		isDescriptionCompleterEnabled,
		issueDescription,
		issueSummary,
		issueTypeLabel,
		shouldBlockStorageUploads,
	]);
	return (
		<FieldContainer width={width}>
			<Placeholder name="description-field" fallback={<Skeleton height={skeletonHeight} />}>
				{isGICPositioningEnabled() ? (
					<WrapperDiv
						tabIndex={-1}
						onFocus={() => {
							if (isGICPositioningEnabled() && onFocusField) {
								onFocusField();
							}
						}}
						onBlur={isGICPositioningEnabled() ? onBlurField : undefined}
						data-testid={
							isGICPositioningEnabled()
								? 'issue-create.common.ui.fields.description-field.wrapper'
								: undefined
						}
					>
						<AsyncRichTextEditView
							key={editorKey.current}
							onChange={onChange}
							onBlur={onBlur}
							// @ts-expect-error - TS2322 - Type 'FieldValue' is not assignable to type 'DocNode | undefined'.
							defaultValue={actualValue}
							mentionProvider={mentionProvider.current}
							// @ts-expect-error - TS2322 - Type '{ viewContext: {}; uploadContext: { clientId: string; serviceHost: string; collection: string; token: string; } | undefined; }' is not assignable to type 'Readonly<{ uploadContext: UploadContext | null; userAuth: UserAuth | null; viewContext: ViewContext | null; }>'.
							mediaContext={mediaProvider}
							shouldFocus={autoFocus}
							isDisabled={isReadOnly}
							assistiveLabel={fieldName}
							sharedEditorConfig={editorConfig}
							{...(isDescriptionCompleterEnabled || shouldBlockStorageUploads
								? {
										setEditorApi,
										contentComponents,
										placeholder: placeholderText,
									}
								: {})}
						/>
					</WrapperDiv>
				) : (
					<AsyncRichTextEditView
						key={editorKey.current}
						onChange={onChange}
						onBlur={onBlur}
						// @ts-expect-error - TS2322 - Type 'FieldValue' is not assignable to type 'DocNode | undefined'.
						defaultValue={actualValue}
						mentionProvider={mentionProvider.current}
						// @ts-expect-error - TS2741 - Property 'userAuth' is missing in type '{ viewContext: {}; uploadContext: { clientId: string; serviceHost: string; collection: string; token: string; } | undefined; }' but required in type 'Readonly<{ uploadContext: UploadContext | null; userAuth: UserAuth | null; viewContext: ViewContext | null; }>'.
						mediaContext={mediaProvider}
						shouldFocus={autoFocus}
						isDisabled={isReadOnly}
						assistiveLabel={fieldName}
						sharedEditorConfig={editorConfig}
						{...(isDescriptionCompleterEnabled || shouldBlockStorageUploads
							? {
									setEditorApi,
									contentComponents,
									placeholder: placeholderText,
								}
							: {})}
					/>
				)}
			</Placeholder>
		</FieldContainer>
	);
};

export default withFormField({
	validator: validateDescriptionField,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element' is not assignable to parameter of type 'AbstractComponent<ComponentProps<DescriptionFieldConfig, FieldValue>, any>'.
})<DescriptionFieldConfig, FieldValue>(DescriptionField);
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { mutateADFField } from './utils';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WrapperDiv = styled.div({
	outline: 'none',
});
WrapperDiv.displayName = 'WrapperDiv';
